import { useMemo } from 'react';
import { Chip, Stack, Typography } from '@mui/material';
import { fontFamily } from '@/@mui/tokens/font-family';
import { fontWeights } from '@/@mui/tokens/font-weights';
import type { PriceTypes } from '@/models/catalog/course';
import { formatMoney } from '@/utils/format-money';

export interface PricesProps {
  discountPercentage: number;
  installments: number;
  offersQuantity: number;
  originalPrice: number;
  priceType: PriceTypes;
  priceWithDiscount: number;
}

export const Prices = ({
  discountPercentage,
  installments,
  offersQuantity,
  originalPrice,
  priceType,
  priceWithDiscount,
}: PricesProps) => {
  const formattedDiscount = () => {
    const discountLabel =
      discountPercentage === 100 || offersQuantity === 1
        ? Math.floor(discountPercentage)
        : `até ${Math.floor(discountPercentage)}`;

    return `${discountLabel}% OFF`;
  };

  const hasKnownPrice = useMemo(() => {
    return !!(
      priceType !== 'upon_request' &&
      priceWithDiscount &&
      /\d+/.test(`${priceWithDiscount}`)
    );
  }, [priceType, priceWithDiscount]);

  const originalPriceValue =
    installments && priceType === 'installments'
      ? originalPrice / installments
      : originalPrice;

  const uponRequest = priceType === 'upon_request';
  const monthly = priceType === 'monthly';
  const isFree = priceWithDiscount === 0 && originalPrice === 0;

  return (
    <Stack direction="row" alignItems="flex-end">
      {uponRequest && (
        <Typography
          fontWeight="semibold"
          fontFamily={fontFamily.fontFamilyCarbona}
        >
          Preço sob consulta
        </Typography>
      )}
      {!uponRequest && (
        <Stack flex={1}>
          {discountPercentage > 0 && !uponRequest && originalPriceValue > 0 && (
            <Typography variant="body2" color="neutral.500">
              {hasKnownPrice && offersQuantity > 1 && 'a partir de '}
              <span style={{ textDecoration: 'line-through' }}>
                {installments > 1 && `${installments}x`}{' '}
                {`${formatMoney(originalPriceValue)}${monthly ? '/mês' : ''}`}
              </span>
            </Typography>
          )}
          <Typography
            fontWeight="semibold"
            fontFamily={fontFamily.fontFamilyCarbona}
          >
            {installments > 1 && priceWithDiscount > 0 && `${installments}x`}{' '}
            {isFree
              ? 'Gratuito'
              : `${formatMoney(priceWithDiscount)}${monthly ? '/mês' : ''}`}
          </Typography>
        </Stack>
      )}
      {discountPercentage > 0 && !uponRequest && !isFree && (
        <Chip
          label={formattedDiscount()}
          color="success"
          sx={{
            border: 'none',
            fontWeight: fontWeights.fontWeightSemibold,
            ...(discountPercentage === 100 && {
              backgroundColor: 'success.dark',
              color: 'success.light',
            }),
          }}
        />
      )}
    </Stack>
  );
};
